/**
 * AdminGlobalAnalyticsPage — cross-organization strategic dashboard.
 *
 * Strict super-admin access (the `/admin/*` API surface already enforces
 * `require_admin`; the frontend simply shouldn't surface this navigation
 * item to non-admins, which DashboardLayout already does for /admin).
 *
 * Sections:
 *   - 6 KPI cards (orgs total / active / inactive, jobs, candidates, conversion)
 *   - Bar chart: top 10 orgs by analysed-in-period
 *   - Line chart: daily imported vs analysed
 *   - Pie chart: plan distribution
 *   - Top 5 orgs table
 *   - Inactive 5 orgs table (churn watch)
 *   - Alerts panel
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
    Loader2, Building2, Briefcase, Users, TrendingUp, AlertTriangle, ArrowLeft,
    Activity, PieChart as PieIcon, BarChart3, ListOrdered, TimerReset,
} from 'lucide-react';
import { adminApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import BackButton from '../../components/BackButton';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { getPlanLabel } from '../../lib/planLabels';
import { toast } from 'sonner';

const PERIODS = [7, 30, 90];
const PIE_COLORS = ['#002FA7', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#06B6D4'];


function StatCard({ icon: Icon, label, value, accent = 'indigo', testid }) {
    const tones = {
        indigo:  'bg-indigo-50 text-indigo-700 border-indigo-100',
        emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
        rose:    'bg-rose-50 text-rose-700 border-rose-100',
        amber:   'bg-amber-50 text-amber-700 border-amber-100',
        sky:     'bg-sky-50 text-sky-700 border-sky-100',
        violet:  'bg-violet-50 text-violet-700 border-violet-100',
    };
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={testid}>
            <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center border ${tones[accent]}`}>
                    <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</p>
                    <p className="text-2xl font-bold text-[#0A0A0A] mt-1">{value}</p>
                </div>
            </div>
        </div>
    );
}

function SectionCard({ title, subtitle, icon: Icon, testid, children }) {
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={testid}>
            <div className="flex items-start gap-2 mb-4">
                {Icon && <Icon className="w-4 h-4 text-[#6B7280] mt-1" />}
                <div>
                    <h3 className="text-sm font-semibold text-[#0A0A0A] font-['Outfit']">{title}</h3>
                    {subtitle && <p className="text-xs text-[#6B7280] mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {children}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString();
    } catch {
        return '—';
    }
}

export default function AdminGlobalAnalyticsPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [period, setPeriod] = useState(30);
    const [orgFilter, setOrgFilter] = useState('all');
    const [organizations, setOrganizations] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.getOrganizationsList().then((r) => setOrganizations(r.data.organizations || [])).catch(() => {});
    }, []);

    useEffect(() => {
        loadAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, orgFilter]);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
            const params = { period_days: period };
            if (orgFilter !== 'all') params.organization_id = orgFilter;
            const res = await adminApi.getGlobalAnalytics(params);
            setData(res.data);
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setLoading(false);
        }
    };

    const summary = data?.summary || {};
    const topOrgs = data?.top_organizations || [];
    const inactiveOrgs = data?.inactive_organizations_list || [];
    const timeseries = data?.timeseries || [];
    const planDist = data?.plan_distribution || [];
    const alerts = data?.alerts || [];

    // Bar chart data: top 10 orgs by analyzed_in_period (descending).
    const barData = useMemo(() => {
        const all = data?.per_organization || [];
        return [...all]
            .sort((a, b) => (b.analyzed_in_period || 0) - (a.analyzed_in_period || 0))
            .slice(0, 10)
            .map((o) => ({
                name: (o.name || '—').slice(0, 18),
                jobs: o.jobs_count || 0,
                analyzed: o.analyzed_in_period || 0,
                candidates: o.candidates_count || 0,
            }));
    }, [data]);

    const pieData = useMemo(
        () => {
            const total = planDist.reduce((s, p) => s + (p.count || 0), 0);
            return planDist.map((p) => ({
                name: getPlanLabel(p.plan, t),
                value: p.count,
                percent: total > 0 ? (p.count / total) : 0,
            }));
        },
        [planDist, i18n.language, t],
    );

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="admin-global-analytics-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div>
                        <BackButton to="/dashboard/admin" data-testid="back-to-admin" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                            {t('global_analytics.title')}
                        </h1>
                        <p className="text-[#4B5563] text-sm">{t('global_analytics.subtitle')}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
                        <SelectTrigger className="w-full sm:w-[140px]" data-testid="period-filter">
                            <SelectValue placeholder={t('global_analytics.period_label')} />
                        </SelectTrigger>
                        <SelectContent>
                            {PERIODS.map((p) => (
                                <SelectItem key={p} value={String(p)}>{t(`global_analytics.period_${p}`)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={orgFilter} onValueChange={setOrgFilter}>
                        <SelectTrigger className="w-full sm:w-[220px]" data-testid="org-filter">
                            <SelectValue placeholder={t('global_analytics.filter_by_org')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('global_analytics.all_organizations')}</SelectItem>
                            {organizations.map((o) => (
                                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard
                    icon={Building2}
                    label={t('global_analytics.kpi_total_orgs')}
                    value={summary.total_organizations ?? 0}
                    accent="indigo"
                    testid="kpi-total-orgs"
                />
                <StatCard
                    icon={Activity}
                    label={t('global_analytics.kpi_active_orgs', { period })}
                    value={summary.active_organizations ?? 0}
                    accent="emerald"
                    testid="kpi-active-orgs"
                />
                <StatCard
                    icon={TimerReset}
                    label={t('global_analytics.kpi_inactive_orgs')}
                    value={summary.inactive_organizations ?? 0}
                    accent="amber"
                    testid="kpi-inactive-orgs"
                />
                <StatCard
                    icon={Briefcase}
                    label={t('global_analytics.kpi_total_jobs')}
                    value={`${summary.total_jobs ?? 0} (${summary.active_jobs ?? 0})`}
                    accent="sky"
                    testid="kpi-total-jobs"
                />
                <StatCard
                    icon={Users}
                    label={t('global_analytics.kpi_total_candidates')}
                    value={summary.total_candidates ?? 0}
                    accent="violet"
                    testid="kpi-total-candidates"
                />
                <StatCard
                    icon={TrendingUp}
                    label={t('global_analytics.kpi_conversion')}
                    value={`${summary.conversion_rate ?? 0}%`}
                    accent={summary.conversion_rate >= 50 ? 'emerald' : 'rose'}
                    testid="kpi-conversion"
                />
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SectionCard
                    title={t('global_analytics.chart_jobs_per_org_title')}
                    subtitle={t('global_analytics.chart_jobs_per_org_subtitle')}
                    icon={BarChart3}
                    testid="chart-jobs-per-org"
                >
                    {barData.length === 0 ? (
                        <div className="h-56 flex items-center justify-center text-sm text-[#9CA3AF]">{t('global_analytics.no_data')}</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={barData} margin={{ top: 24, right: 10, left: -10, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                <Tooltip cursor={{ fill: 'rgba(0,47,167,0.05)' }} contentStyle={{ fontSize: '12px' }} />
                                <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: '11px', paddingBottom: 4 }} />
                                <Bar dataKey="analyzed" fill="#002FA7" name={t('global_analytics.analyzed')} radius={[3, 3, 0, 0]} />
                                <Bar dataKey="jobs" fill="#7C3AED" name={t('global_analytics.col_jobs')} radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </SectionCard>

                <SectionCard
                    title={t('global_analytics.chart_timeseries_title')}
                    subtitle={t('global_analytics.chart_timeseries_subtitle')}
                    icon={Activity}
                    testid="chart-timeseries"
                >
                    {timeseries.length === 0 ? (
                        <div className="h-56 flex items-center justify-center text-sm text-[#9CA3AF]">{t('global_analytics.no_data')}</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <LineChart data={timeseries} margin={{ top: 24, right: 10, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                                <Tooltip contentStyle={{ fontSize: '12px' }} />
                                <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: '11px', paddingBottom: 4 }} />
                                <Line type="monotone" dataKey="imported" stroke="#7C3AED" strokeWidth={2} name={t('global_analytics.imported')} dot={{ r: 2 }} />
                                <Line type="monotone" dataKey="analyzed" stroke="#10B981" strokeWidth={2} name={t('global_analytics.analyzed')} dot={{ r: 2 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </SectionCard>

                <SectionCard
                    title={t('global_analytics.chart_plan_title')}
                    subtitle={t('global_analytics.chart_plan_subtitle')}
                    icon={PieIcon}
                    testid="chart-plan"
                >
                    {pieData.length === 0 ? (
                        <div className="h-56 flex items-center justify-center text-sm text-[#9CA3AF]">{t('global_analytics.no_data')}</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={240}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%" cy="50%"
                                    innerRadius={50} outerRadius={85}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ percent }) => percent >= 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                                    labelLine={false}
                                >
                                    {pieData.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ fontSize: '12px' }}
                                    formatter={(value, name, entry) => {
                                        const pct = entry?.payload?.percent ?? 0;
                                        return [`${value} (${(pct * 100).toFixed(1)}%)`, name];
                                    }}
                                />
                                <Legend
                                    wrapperStyle={{ fontSize: '11px' }}
                                    formatter={(value, entry) => {
                                        const pct = entry?.payload?.percent ?? 0;
                                        return `${value} · ${(pct * 100).toFixed(0)}%`;
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </SectionCard>
            </div>

            {/* Rankings + Alerts row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SectionCard
                    title={t('global_analytics.top_orgs_title')}
                    subtitle={t('global_analytics.top_orgs_subtitle')}
                    icon={ListOrdered}
                    testid="top-orgs-section"
                >
                    {topOrgs.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF]">{t('global_analytics.no_data')}</p>
                    ) : (
                        <ul className="divide-y divide-[#F3F4F6]" data-testid="top-orgs-list">
                            {topOrgs.map((o, i) => (
                                <li key={o.id} className="flex items-center justify-between py-2 text-sm">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-xs font-bold text-[#9CA3AF] w-5">#{i + 1}</span>
                                        <span className="truncate text-[#0A0A0A]">{o.name}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-[#4B5563]">
                                        <span>{o.candidates_count || 0} {t('global_analytics.col_candidates').toLowerCase()}</span>
                                        <span className="font-bold text-[#002FA7]">{o.analyzed_in_period || 0}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </SectionCard>

                <SectionCard
                    title={t('global_analytics.inactive_orgs_title')}
                    subtitle={t('global_analytics.inactive_orgs_subtitle')}
                    icon={TimerReset}
                    testid="inactive-orgs-section"
                >
                    {inactiveOrgs.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF]">{t('global_analytics.no_data')}</p>
                    ) : (
                        <ul className="divide-y divide-[#F3F4F6]" data-testid="inactive-orgs-list">
                            {inactiveOrgs.map((o) => (
                                <li key={o.id} className="flex items-center justify-between py-2 text-sm">
                                    <span className="truncate text-[#0A0A0A]">{o.name}</span>
                                    <span className="text-xs text-[#6B7280]">
                                        {o.last_candidate_at ? formatDate(o.last_candidate_at) : '—'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </SectionCard>

                <SectionCard
                    title={t('global_analytics.alerts_title')}
                    subtitle={`${alerts.length}`}
                    icon={AlertTriangle}
                    testid="alerts-section"
                >
                    {alerts.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF]">{t('global_analytics.no_data')}</p>
                    ) : (
                        <ul className="space-y-1.5 max-h-[240px] overflow-y-auto" data-testid="alerts-list">
                            {alerts.slice(0, 30).map((a, i) => (
                                <li
                                    key={i}
                                    className={`text-xs px-2 py-1.5 rounded border ${
                                        a.severity === 'warning'
                                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                                            : 'bg-blue-50 border-blue-200 text-blue-800'
                                    }`}
                                >
                                    <span className="font-medium">{a.organization_name || ''}</span>
                                    {' — '}
                                    {t(`global_analytics.${a.detail_key}`, {
                                        defaultValue: a.type,
                                        value: a.value,
                                    })}
                                </li>
                            ))}
                        </ul>
                    )}
                </SectionCard>
            </div>
        </div>
    );
}
